/**
 * `<StudioChooser>` — staff-person-login-studio-switcher (2026-09-27). What
 * the layout renders INSTEAD of a page when `/api/whoami` answers `studio:
 * null`: the person is staff of several studios and this tab has not said
 * which one it is in (no `/s/<slug>/` prefix), or it named a studio they are
 * not staff of (a stale tab, a mistyped URL — the server refuses both the
 * same way and tells us the slug it refused).
 *
 * # Why real anchors, and why a full page load
 *
 * Every card is a plain `<a href="/s/<slug>/…">`, not a router `<Link>` or a
 * `navigate()` call. Two reasons, both load-bearing:
 *
 *   1. The basename is read ONCE at boot (`main.tsx`); the tab must re-boot
 *      under the chosen prefix for every later link and every `X-Layer-Studio`
 *      header to agree with the URL. A router transition would leave the tab
 *      mounted with an empty basename and a chooser-shaped whoami in memory.
 *   2. Working two studios at once is the point of the phase: a Cmd-click or
 *      middle-click on a card opens that studio in a second tab, and the
 *      browser only does that for a real anchor with a real `href`.
 *
 * `carried` is the basename-stripped path + search + hash the tab was opened
 * on (`/` when there is nothing to carry — `lib/studio-chooser.ts`), so a deep
 * link like `/members/1?tab=2` still reaches the member after the pick.
 *
 * Pure over its props: the layout computes them, and nothing here reads
 * `window`.
 */

import type { WhoamiStudioSummary } from "@layer/dashboard/types";
import { studioPath } from "../lib/studio-scope";

export interface StudioChooserProps {
  /** Every studio this person belongs to — `whoami.studios`. */
  readonly studios: ReadonlyArray<WhoamiStudioSummary>;
  /** The slug the URL named when it named a studio they are not in; null otherwise. */
  readonly requestedSlug: string | null;
  /** The inner path (+ search + hash) each card lands on; `/` for the studio root. */
  readonly carried: string;
}

export function StudioChooser({
  studios,
  requestedSlug,
  carried,
}: StudioChooserProps): JSX.Element {
  return (
    <div className="studio-chooser">
      <h1 className="page-title">Choose a studio</h1>
      {requestedSlug !== null ? (
        <p className="studio-chooser-notice" role="status">
          You&apos;re not staff of <code>{requestedSlug}</code>. Pick one of
          your studios.
        </p>
      ) : (
        <p className="subtle">
          You&apos;re staff of {studios.length} studios. Open one here, or
          open several in their own tabs to work them side by side.
        </p>
      )}
      <ul className="studio-chooser-list">
        {studios.map((s) => (
          <li key={s.id}>
            <a className="studio-chooser-card" href={studioPath(s.slug, carried)}>
              <span className="studio-chooser-name">{s.name}</span>
              <span className="studio-chooser-slug">/s/{s.slug}/</span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
